import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaVideo, FaCopy } from 'react-icons/fa';
import { Button, Badge, DataTable, StatCard } from '../../Components/UI';
import { fetchSessions, buildPatientLink } from '../../Services/videoConsultation';
import exportToExcel from '../../UtilityHookFunctions/exportToExcel';

const statusVariant = {
  Scheduled: 'primary',
  Live: 'success',
  Ended: 'default',
  Cancelled: 'danger',
};

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'Scheduled', label: 'Upcoming' },
  { key: 'Live', label: 'Live' },
  { key: 'Ended', label: 'Completed' },
];

export default function VideoConsultationList() {
  const navigate = useNavigate();

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const data = await fetchSessions({ pageSize: 100 });
        if (!cancelled) setSessions(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.error || 'Could not load video consultations');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const counts = useMemo(
    () => ({
      all: sessions.length,
      Scheduled: sessions.filter((s) => s.status === 'Scheduled').length,
      Live: sessions.filter((s) => s.status === 'Live').length,
      Ended: sessions.filter((s) => s.status === 'Ended').length,
    }),
    [sessions]
  );

  const rows = useMemo(() => {
    const base = activeTab === 'all' ? sessions : sessions.filter((s) => s.status === activeTab);
    const term = search.trim().toLowerCase();

    return base
      .filter(
        (s) =>
          !term ||
          String(s.patientName || '').toLowerCase().includes(term) ||
          String(s.doctorName || '').toLowerCase().includes(term) ||
          String(s.bookDate || '').toLowerCase().includes(term)
      )
      .map((s) => ({
        id: s._id,
        appointmentId: s.appointmentId,
        guestToken: s.guestToken,
        patientName: s.patientName,
        doctorName: s.doctorName,
        when: `${s.bookDate || '—'}${
          s.bookTimeSlot?.startTime ? ` · ${s.bookTimeSlot.startTime}` : ''
        }`,
        status: s.status,
      }));
  }, [sessions, activeTab, search]);

  const copyLink = async (guestToken) => {
    const link = buildPatientLink(guestToken);
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Patient link copied');
    } catch (e) {
      window.prompt('Copy the patient link:', link);
    }
  };

  const headers = [
    { label: 'Patient', key: 'patientName' },
    { label: 'Doctor', key: 'doctorName' },
    { label: 'Scheduled', key: 'when' },
    {
      label: 'Status',
      key: 'status',
      render: (val) => <Badge variant={statusVariant[val] || 'default'}>{val}</Badge>,
    },
    {
      label: 'Action',
      key: 'appointmentId',
      render: (appointmentId, row) => (
        <div className="flex items-center gap-2">
          <Button
            size="xs"
            disabled={!appointmentId || row.status === 'Ended'}
            onClick={() => navigate(`/video-consultation/${appointmentId}`)}
            icon={<FaVideo className="h-3.5 w-3.5" />}
          >
            {row.status === 'Live' ? 'Rejoin' : 'Join Call'}
          </Button>
          <Button
            size="xs"
            variant="outline"
            disabled={!row.guestToken}
            onClick={() => copyLink(row.guestToken)}
            icon={<FaCopy className="h-3.5 w-3.5" />}
          >
            Copy link
          </Button>
        </div>
      ),
    },
  ];

  if (error) {
    return (
      <div className="p-6">
        <div className="mx-auto max-w-lg rounded-xl border border-rose-200 bg-rose-50 p-6 text-center">
          <p className="font-medium text-rose-700">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 p-4 md:p-8">
      <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
        <h1 className="flex items-center gap-2 text-xl font-bold tracking-tight text-slate-800">
          <FaVideo className="text-violet-600" /> Video Consultations
        </h1>
        <p className="mt-0.5 text-xs text-slate-500">
          Online appointments and their consultation rooms
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {TABS.map((t) => (
          <StatCard
            key={t.key}
            title={t.label}
            value={counts[t.key] || 0}
            icon={<FaVideo />}
            color={t.key === 'Live' ? 'emerald' : 'violet'}
            onClick={() => setActiveTab(t.key)}
            className={activeTab === t.key ? 'ring-2 ring-violet-500/40' : ''}
          />
        ))}
      </div>

      <div className="rounded-2xl border border-slate-200/80 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 p-4">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by patient, doctor or date…"
            className="w-full max-w-sm rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
          />
          <Button
            size="sm"
            variant="outline"
            disabled={loading || !rows.length}
            onClick={() =>
              exportToExcel(rows, 'Video_Consultations', [
                { key: 'patientName', label: 'Patient' },
                { key: 'doctorName', label: 'Doctor' },
                { key: 'when', label: 'Scheduled' },
                { key: 'status', label: 'Status' },
                { key: 'guestToken', label: 'Patient Link', format: (token) => (token ? buildPatientLink(token) : '') },
              ])
            }
          >
            Export to Excel
          </Button>
        </div>
        <DataTable headers={headers} data={rows} isLoading={loading} emptyMessage="No video consultations yet." />
      </div>
    </div>
  );
}
